/**
 * El plan de pago que alguien eligió en faktelio.com antes de tener cuenta.
 *
 * ## Por qué existe
 *
 * Hasta el 2026-10-19 los botones de los planes de pago del sitio llevaban a
 * `/plan` a secas. Quien ya tenía sesión llegaba bien; quien no, pasaba por
 * crear la cuenta, confirmar el correo —el enlace se abre en OTRA pestaña— y
 * entrar, y al final aterrizaba en el panel general sin rastro del plan que
 * había elegido. Tenía que buscarlo él. Es la fricción que el propietario pidió
 * quitar: pulsar «Contratar Business» tiene que acabar en la pantalla de pago
 * de Business.
 *
 * ## Lo que esto NO es
 *
 * **No es una compra ni concede nada.** Es una preferencia de interfaz: qué
 * botón enseñar primero. El checkout sigue siendo el `POST` de siempre, con
 * sesión de OWNER y datos fiscales declarados (`D3-b`), y el plan lo sube el
 * webhook cuando Stripe confirma el cobro. Si alguien escribe a mano
 * `?contratar=business`, lo único que consigue es ver ese botón arriba.
 *
 * **No es un destino libre.** El valor se busca en una lista cerrada de dos
 * entradas, igual que las rutas de `destino.ts`: lo que no esté en la lista no
 * existe.
 *
 * **No es un dato fiscal** (`ADR-0004` X3): se guarda en el navegador porque
 * tiene que sobrevivir a la pestaña del enlace de confirmación, y si el
 * navegador lo pierde —modo privado, almacenamiento bloqueado— no se rompe
 * nada: la pantalla del plan enseña los dos botones, que es lo que había.
 */
import type { PlanDePago } from "../api/cliente";

/** El parámetro que pone el sitio público: `/plan?contratar=business`. */
export const PARAMETRO = "contratar";

/**
 * La lista cerrada. Las claves son las del sitio —`apps/site/src/content/
 * aplicacion.ts` las compone—, y los valores, los planes de la API.
 */
export const PLAN_DEL_ENLACE: Readonly<Record<string, PlanDePago>> = {
  "factura-plus": "FACTURA_PLUS",
  business: "BUSINESS",
};

const CLAVE = "faktelio.plan-elegido";

/**
 * Una semana, lo mismo que el enlace de confirmación del correo (`OD-0027`).
 * Pasado ese plazo la elección ya no es de esta visita, y enseñar un plan
 * elegido hace un mes se leería como un error.
 */
export const CADUCA_MS = 7 * 24 * 60 * 60 * 1000;

/** El plan que pide la URL, o `null`. */
export function planDelEnlace(search: string): PlanDePago | null {
  const valor = new URLSearchParams(search).get(PARAMETRO);
  if (valor === null) return null;
  return Object.prototype.hasOwnProperty.call(PLAN_DEL_ENLACE, valor) ? (PLAN_DEL_ENLACE[valor] ?? null) : null;
}

function almacen(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function recordarPlanElegido(plan: PlanDePago, ahora: number): void {
  try {
    almacen()?.setItem(CLAVE, JSON.stringify({ plan, en: ahora }));
  } catch {
    // Sin almacenamiento se sigue: esta visita ya lo sabe por la URL.
  }
}

/** El plan elegido en una visita anterior, si sigue vigente. */
export function planElegidoPendiente(ahora: number): PlanDePago | null {
  let crudo: string | null = null;
  try {
    crudo = almacen()?.getItem(CLAVE) ?? null;
  } catch {
    return null;
  }
  if (crudo === null) return null;
  try {
    const v = JSON.parse(crudo) as { plan?: unknown; en?: unknown };
    // Lo guardado se vuelve a buscar en la MISMA lista cerrada: el navegador
    // es del usuario, y lo que haya ahi puede haberlo escrito cualquiera.
    const plan = Object.values(PLAN_DEL_ENLACE).find((p) => p === v.plan);
    const edad = typeof v.en === "number" ? ahora - v.en : -1;
    if (plan !== undefined && edad >= 0 && edad < CADUCA_MS) return plan;
  } catch {
    // Ilegible: se trata como si no hubiera nada, y se borra.
  }
  olvidarPlanElegido();
  return null;
}

/** Se olvida al abrir el pago o al ver una suscripción viva: ya no hace falta. */
export function olvidarPlanElegido(): void {
  try {
    almacen()?.removeItem(CLAVE);
  } catch {
    // Nada que hacer: caduca solo.
  }
}
